/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import './style/vatDeclaration.scss';

import {AwesomeIcon, Checkbox, Link, Loader} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import classNames from 'classnames';

import React, {Fragment, MouseEventHandler, ReactElement, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import VatDeclarationItem from '../../models/vatDeclarationItemModel';

import {
    AccessKey,
    BolInvoice,
    BolInvoiceItemType,
    CategoryType,
    ExportProcessStatus,
    ProcessingStatus,
    TaxTotalsData,
    VatDeclarationInvalidation,
    VatPercentageData
} from '../../util/interfaces';

import {calculateRevenues, calculateTaxes, getBolInvoices, groupVatItems} from '../../util/vatDeclarationUtil';

import {parseMoney} from '../../util/utils';

import ProcessStatus from '../../models/processStatusModel';

import CountryFlag from '../misc/countryFlag';

import VatPeriodSelectController from './vatPeriodSelect/vatPeriodSelectController';

import {BluemateButton} from '../controls';

import InfoIcon from '../icon/infoIcon';

import ExportProcess from '../../models/exportProcessModel';

import {AppContext} from '../misc/appContext';

import {DUTCH_DATE_FORMAT} from '../../util/constants';

import useRight from '../misc/useRight';

import InfoBanner from '../misc/infoBanner';

interface Props {
    onFetch: MouseEventHandler;
    onChangePeriod: (start: IDate, end: IDate, isCustom?: boolean) => void;
    declarationItems: Record<string, VatDeclarationItem[]>;
    onCloseInfo: MouseEventHandler;
    onDownloadInvoice: (invoiceId: string, fileType: 'pdf' | 'excel') => MouseEventHandler;
    onRecalculate: MouseEventHandler;
    onNavigateToProducts: MouseEventHandler;
    onToggleMail: MouseEventHandler;
    onDownloadExport: (exportProcess: ExportProcess) => MouseEventHandler;
    onExportDeclaration: MouseEventHandler;
    period: {start: IDate; end: IDate} | null;
    loading: boolean;
    loadingExport: boolean;
    invalidated: VatDeclarationInvalidation | null;
    processStatus: ProcessStatus | null;
    showInfo: boolean;
    defaultVats: Record<string, number>;
    exportProcesses: Array<ExportProcess>;
}

const mailKey = 'notify_vat_declaration_finished';

function sortItems(items: Array<VatDeclarationItem>) {
    items.sort((itemA: VatDeclarationItem, itemB: VatDeclarationItem) => {
        if ([BolInvoiceItemType.TURNOVER, BolInvoiceItemType.TURNOVER_BOL].includes(itemA.category)) return -1;
        else if ([BolInvoiceItemType.TURNOVER, BolInvoiceItemType.TURNOVER_BOL].includes(itemB.category)) return 1;
        return itemA.categoryTranslation.localeCompare(itemB.categoryTranslation);
    });
}

export default function VatDeclaration(props: Props) {
    const {t} = useTranslation();
    const preTaxInfo = t(
        'vatDeclaration.preTaxInfo',
        'De voorbelasting is de BTW die je zelf hebt betaald over de gemaakte kosten bij Bol. Deze BTW krijg je van de Belastingdienst terug via de BTW-aangifte.'
    );
    const taxInfo = t(
        'vatDeclaration.taxInfo',
        'De af te dragen BTW is de BTW die je ontvangen hebt van jouw klanten. Deze BTW draag je af aan de Belastingdienst via de BTW-aangifte.'
    );
    const reversedInfo = t(
        'vatDeclaration.reversedInfo',
        'Als je bestellingen van buitenlandse zakelijke klanten ontvangt, dan kan het zijn dat je de BTW hebt verlegd naar de klant. Tijdens de BTW aangifte moet je de omzet opgeven waarvan de BTW verlegd is.'
    );
    const context = useContext(AppContext);
    const [canExport, canDownload] = useRight(
        AccessKey.VAT_DECLARATION_EXPORT,
        AccessKey.VAT_DECLARATION_DOWNLOAD_SPEC
    );
    const {
        declarationItems,
        period,
        onFetch,
        loading,
        loadingExport,
        invalidated,
        processStatus,
        onChangePeriod,
        onCloseInfo,
        showInfo,
        onDownloadInvoice,
        onRecalculate,
        onNavigateToProducts,
        defaultVats,
        onToggleMail,
        onExportDeclaration,
        onDownloadExport,
        exportProcesses
    } = props;
    const [revenueItems, costItems, lastOrderDate] = useMemo(() => {
        const groupedItems: VatDeclarationItem[] = groupVatItems(Object.assign({}, declarationItems));
        const _costItems: VatDeclarationItem[] = [];
        const _revenueItems: VatDeclarationItem[] = [];
        let lastOrderDate: IDate | null = null;
        for (const item of groupedItems) {
            if (item.lastOrderDate) {
                if (!lastOrderDate) lastOrderDate = item.lastOrderDate;
                else if (lastOrderDate.isBefore(item.lastOrderDate)) lastOrderDate = item.lastOrderDate;
            }
            if (item.categoryType === CategoryType.COSTS) _costItems.push(item);
            else _revenueItems.push(item);
        }
        sortItems(_revenueItems);
        sortItems(_costItems);
        return [_revenueItems, _costItems, lastOrderDate];
    }, [declarationItems]);
    const showReverseDisclaimer = useMemo(() => {
        return context.accountContext.account?.country !== 'Nederland';
    }, [context.accountContext.account]);

    function determineInvoicePeriod(bolInvoice: BolInvoice): [IDate, IDate] {
        let periodStart = new IDate(bolInvoice.period_start);
        let periodEnd = new IDate(bolInvoice.period_end);
        if (periodStart.isBefore(period!.start, 'd')) periodStart = period!.start;
        if (periodEnd.isAfter(period!.end, 'd')) periodEnd = period!.end;
        return [periodStart, periodEnd];
    }

    function renderInfo() {
        return (
            <InfoBanner
                className='general-info-container'
                onClose={onCloseInfo}
                persistDismiss={false}
                variant={'warning'}
            >
                {t(
                    'vatDeclaration.generalInfo',
                    'We berekenen deze cijfers aan de hand van jouw maandelijkse factuurspecificaties van Bol. Deze vullen wij nog aan met de BTW percentages van jouw producten, zoals ingesteld in Bluemate en met eventuele verlegd BTW via Bluemate. Zorg dus dat de BTW percentages van producten in Bluemate goed zijn ingesteld.'
                )}
            </InfoBanner>
        );
    }

    function renderReverseDisclaimer() {
        return (
            <InfoBanner variant='warning' persistDismiss={false}>
                {t(
                    'vatDeclaration.reverseDisclaimer',
                    'Voor verkooppartners buiten Nederland wordt de BTW van de kosten door Bol verlegd. In de factuurspecificatie van Bol wordt dit niet duidelijk aangegeven, hierdoor kunnen wij de BTW bedragen van de kosten (voorbelasting) niet goed berekenen. Wij raden aan een boekhouder/accountant te raadplegen voor het doen van je BTW-aangifte.'
                )}
            </InfoBanner>
        );
    }

    function renderLastOrderDate() {
        const today = new IDate();
        const prevMonth = new IDate().subtract(1, 'month');
        const calculatingPrevPeriod = period?.end.isAt(prevMonth, 'month');
        const availableFrom = new IDate().day(9);
        if (today.isAtOrAfter(availableFrom, 'day') || !calculatingPrevPeriod || !lastOrderDate) return null;
        const dateFormat = 'D MMMM YYYY';
        const lastOrderString = lastOrderDate.format(dateFormat);
        const availableFromString = availableFrom.format(dateFormat);
        return (
            <InfoBanner variant='warning' persistDismiss={false}>
                <div className='last-order-date-info'>
                    <label className='info-title'>
                        {t(
                            'vatDeclaration.lastOrderTitle',
                            'Data van bol ontvangen tot {{lastOrderString}} – Volledige gegevens waarschijnlijk beschikbaar vanaf {{availableFromString}}',
                            {lastOrderString, availableFromString}
                        )}
                    </label>
                    <div className='info-container'>
                        <InfoIcon />
                        <p className='info-message'>
                            {t(
                                'vatDeclaration.lastOrderInfo',
                                'Voor deze berekening gebruiken we dezelfde factuurspecificaties van bol in Excel die jij ook ontvangt. We vullen deze aan met de BTW-gegevens van Bluemate. De data die we hebben opgehaald bevat orders tot en met {{lastOrderString}}. Dit kan twee dingen betekenen:',
                                {lastOrderString}
                            )}
                            <ol className='options-list'>
                                <li>
                                    <b>{t('vatDeclaration.lastOrderOption1Bold', 'Jouw berekening is nu compleet')}</b>{' '}
                                    {t('vatDeclaration.lastOrderOption1', 'als dit de laatste order van de maand was.')}
                                </li>
                                <li>
                                    <b>
                                        {t(
                                            'vatDeclaration.lastOrderOption2Bold',
                                            'Jouw berekening is nu nog niet compleet'
                                        )}
                                    </b>{' '}
                                    {t(
                                        'vatDeclaration.lastOrderOption2',
                                        'als er na {{lastOrderString}} nog orders waren. In dat geval komt rond {{availableFromString}} een nieuwe Excel-sheet beschikbaar bij bol. Voer deze berekening vanaf {{availableFromString}} opnieuw uit.',
                                        {lastOrderString, availableFromString}
                                    )}
                                </li>
                            </ol>
                        </p>
                    </div>
                </div>
            </InfoBanner>
        );
    }

    function renderPeriod() {
        const dateFormat = 'D MMMM YYYY';
        const isLoading = processStatus?.isActive() === true;
        return (
            <>
                <div className='period-row-container'>
                    <div className={classNames('period-container', isLoading && 'loading')}>
                        {period === null ? (
                            <label className='period grid-col-3'>
                                {t('vatDeclaration.choosePeriod', 'Kies een periode')}
                            </label>
                        ) : (
                            <>
                                <label className='period'>{period.start.format(dateFormat)}</label>
                                <label className='tm'>{t('vatDeclaration.until', 't/m')}</label>
                                <label className='period'>{period.end.format(dateFormat)}</label>
                            </>
                        )}
                    </div>
                    {renderRefresh()}
                </div>
                {isLoading && (
                    <div className={classNames('period-container', isLoading && 'loading')}>
                        {renderLoadingPeriod()}
                    </div>
                )}
            </>
        );
    }

    function renderRefresh() {
        if (!period) return null;
        return (
            <div
                className={classNames('btn-refresh', loading && 'disabled')}
                onClick={loading ? undefined : onRecalculate}
            >
                <AwesomeIcon name='refresh' className='refresh-icon' />
                <label className='btn-label'>{t('vatDeclaration.recalculate', 'Opnieuw berekenen')}</label>
            </div>
        );
    }

    function renderLoadingPeriod() {
        const {progress, data} = processStatus!;
        const periodStart = new IDate(data.period_start).format('MMMM YYYY');
        const periodEnd = new IDate(data.period_end).format('MMMM YYYY');
        const periodLabel =
            periodStart === periodEnd ? periodStart : `${periodStart} ${t('vatDeclaration.until', 't/m')} ${periodEnd}`;
        const {file_index} = data;
        const loader = (
            <div className='dots-loader'>
                <div className='dot-pulse' />
            </div>
        );
        const mailCheck = (
            <Checkbox
                checked={!!data[mailKey]}
                onCheck={onToggleMail}
                label={t('vatDeclaration.mailNotify', 'Stuur mij een mail zodra de berekening voltooid is')}
            />
        );
        if (file_index)
            return (
                <div className='period-loader'>
                    {loader}
                    <label className='period-label'>
                        {t('vatDeclaration.processingPeriod', 'Bezig met het verwerken van periode {{periodLabel}}', {
                            periodLabel
                        })}
                    </label>
                    <label>
                        {t('vatDeclaration.statusLabel', 'Status:')} {progress}
                    </label>
                    <label>
                        {t('vatDeclaration.canLeaveWindow', 'Je kunt dit venster verlaten en later terugkomen.')}
                    </label>
                    {mailCheck}
                </div>
            );
        else
            return (
                <div className='period-loader'>
                    {loader}
                    <label className='period-label'>
                        {t('vatDeclaration.processingPeriod', 'Bezig met het verwerken van periode {{periodLabel}}', {
                            periodLabel
                        })}
                    </label>
                    <label>
                        {t('vatDeclaration.statusLabel', 'Status:')} {progress}
                    </label>
                    <label>
                        {t('vatDeclaration.canLeaveWindow', 'Je kunt dit venster verlaten en later terugkomen.')}
                    </label>
                    {mailCheck}
                </div>
            );
    }

    function renderGeneralWarnings() {
        return (
            <>
                {invalidated !== null && (
                    <div className='general-warning'>
                        <AwesomeIcon name='triangle-exclamation' className='warn-icon' />
                        <label className='warning'>
                            {t(
                                'vatDeclaration.invalidatedWarning',
                                'Sinds de laatste berekening van de BTW aangifte zijn de {{type}} instellingen gewijzigd. Hierdoor moet de berekening mogelijk opnieuw gedaan worden.',
                                {type: invalidated === VatDeclarationInvalidation.API ? 'API' : 'BTW'}
                            )}{' '}
                            <Link disabled={loading} onClick={onFetch}>
                                {t('vatDeclaration.recalculateNow', 'Nu opnieuw berekenen')}
                            </Link>
                        </label>
                    </div>
                )}
            </>
        );
    }

    function renderTotals() {
        const taxesPerCountry = calculateTaxes(revenueItems, costItems);
        const revenues = calculateRevenues(revenueItems);
        const costs = calculateRevenues(costItems);
        return (
            <div className='totals-container'>
                <label className='part-label'>{t('vatDeclaration.totals', 'Totalen')}</label>
                <div className='totals'>
                    {renderTaxBox('revenueIncl', revenues)}
                    {renderTaxBox('revenueExcl', revenues)}
                    {renderTaxBox('tax', taxesPerCountry, taxInfo)}
                    {renderTaxBox('reversedIncl', revenues, reversedInfo)}
                    {renderTaxBox('revenueIncl', costs, undefined, true)}
                    {renderTaxBox('revenueExcl', costs, undefined, true)}
                    {renderTaxBox('preTax', taxesPerCountry, preTaxInfo)}
                </div>
            </div>
        );
    }

    function renderTaxBox(
        valueKey: keyof TaxTotalsData,
        taxesPerCountry: Record<string, TaxTotalsData>,
        info?: string,
        isCosts?: boolean
    ) {
        let title: string;
        if (valueKey === 'tax') title = t('vatDeclaration.box.tax', 'Af te dragen BTW');
        else if (valueKey === 'preTax') title = t('vatDeclaration.box.preTax', 'Voorbelasting');
        else if (valueKey === 'revenueIncl')
            title = isCosts
                ? t('vatDeclaration.box.costsIncl', 'Kosten incl. BTW')
                : t('vatDeclaration.box.revenueIncl', 'Omzet incl. BTW');
        else if (valueKey === 'reversedIncl') title = t('vatDeclaration.box.reversedIncl', 'Omzet met verlegde BTW');
        else
            title = isCosts
                ? t('vatDeclaration.box.costsExcl', 'Kosten excl. BTW')
                : t('vatDeclaration.box.revenueExcl', 'Omzet excl. BTW');
        const loadLabel = t('vatDeclaration.box.calculating', 'Wordt berekend...');
        const noDataLabel = t('vatDeclaration.box.noData', 'Geen beschikbare data gevonden');
        const countryCodes = Object.keys(taxesPerCountry);
        let hasData: boolean = false;
        for (const countryCode of countryCodes) {
            const vatPercentages = Object.keys(taxesPerCountry[countryCode][valueKey]);
            if (!vatPercentages.length) continue;
            for (const vatPercentage of vatPercentages) {
                if (taxesPerCountry[countryCode][valueKey][vatPercentage] > 0) {
                    hasData = true;
                    break;
                }
            }
        }
        if (!hasData && valueKey === 'reversedIncl') return null;
        return (
            <div className='tax-box'>
                <div className='box-header'>
                    <label className='box-label'>{title}</label>
                    {!!info && (
                        <div className='decl-info-container'>
                            <InfoIcon />
                            <p className='info-text'>{info}</p>
                        </div>
                    )}
                </div>
                <div className='box-content'>
                    {loading ? (
                        <label className='load-label'>{loadLabel}</label>
                    ) : hasData ? (
                        renderTotalRows(taxesPerCountry, valueKey)
                    ) : (
                        <label className='load-label'>{noDataLabel}</label>
                    )}
                </div>
            </div>
        );
    }

    function renderTotalRows(
        taxesPerCountry: Record<string, TaxTotalsData>,
        dataKey: keyof TaxTotalsData
    ): Array<ReactElement> {
        const rows: Array<ReactElement> = [];
        const showVat = dataKey !== 'reversedIncl';
        for (const [countryCode, vatValues] of Object.entries(taxesPerCountry)) {
            const taxTotals = vatValues[dataKey];
            Object.entries(taxTotals).forEach(([vatPercentage, value]: [string, number], index: number) => {
                if (!value) return;
                rows.push(
                    <Fragment key={`${countryCode}-${index}`}>
                        <label className='country'>
                            <CountryFlag countryCode={countryCode} />
                            {showVat ? `${vatPercentage}% ${t('vatDeclaration.vatLabel', 'BTW')}` : ''}
                        </label>
                        <label className='amount'>&euro;{parseMoney(value)}</label>
                    </Fragment>
                );
            });
        }
        return rows;
    }

    function renderLists() {
        return (
            <>
                {renderList(t('vatDeclaration.list.revenueAtBol', 'Omzet bij Bol'), revenueItems)}
                {renderList(t('vatDeclaration.list.costsAtBol', 'Gemaakte kosten bij Bol'), costItems)}
            </>
        );
    }

    function renderList(title: string, items: VatDeclarationItem[]) {
        return (
            <div className='declaration-list'>
                <label className='list-title'>{title}</label>
                <div className='items-table'>
                    <div className='row head'>
                        <label>{t('vatDeclaration.list.category', 'Categorie')}</label>
                        <label />
                        <label>{t('vatDeclaration.list.amountExcl', 'Bedrag excl. BTW')}</label>
                        <label>{t('vatDeclaration.list.vatAmount', 'BTW bedrag')}</label>
                        <label>{t('vatDeclaration.list.total', 'Totaal')}</label>
                    </div>
                    {items.map(renderListItem)}
                </div>
            </div>
        );
    }

    function renderListItem(item: VatDeclarationItem, index: number) {
        return (
            <Fragment key={index}>
                <div className='row bold'>
                    <label>{item.categoryTranslation}</label>
                    <label>{t('vatDeclaration.list.total', 'Totaal')}</label>
                    <label>&euro;{parseMoney(item.getTotalExclVat())}</label>
                    <label>&euro;{parseMoney(item.getTotalVat())}</label>
                    <label>&euro;{parseMoney(item.getTotalInclVat())}</label>
                </div>
                {renderItemData(item)}
            </Fragment>
        );
    }

    function renderItemData(item: VatDeclarationItem): Array<ReactElement> {
        const countryCodes = Array.from(item.getCountryCodes());
        const rows: Array<ReactElement> = [];
        for (const countryCode of countryCodes) {
            Object.entries(item.data[countryCode]).forEach(
                ([vatPercentage, values]: [string, VatPercentageData], index: number) => {
                    if (!values.total_incl_vat && !values.total_vat && !values.total_incl_reversed_vat) return;
                    if (values.total_incl_vat) {
                        const totalExclVat = values.total_incl_vat - values.total_vat;
                        rows.push(
                            <div key={`${countryCode}-${index}`} className='row'>
                                <label />
                                <label className='country'>
                                    <CountryFlag countryCode={countryCode} />
                                    <span>
                                        {vatPercentage}% {t('vatDeclaration.vatLabel', 'BTW')}
                                    </span>
                                </label>
                                <label>&euro;{parseMoney(totalExclVat)}</label>
                                <label>&euro;{parseMoney(values.total_vat)}</label>
                                <label>&euro;{parseMoney(values.total_incl_vat)}</label>
                            </div>
                        );
                    }
                    if (values.total_incl_reversed_vat) {
                        rows.push(
                            <div key={`${countryCode}-${index}`} className='row'>
                                <label />
                                <label className='country'>
                                    <CountryFlag countryCode={countryCode} />
                                    <span>{t('vatDeclaration.vatReversed', 'BTW verlegd')}</span>
                                </label>
                                <label>&euro;{parseMoney(values.total_incl_reversed_vat)}</label>
                                <label>&euro;{parseMoney(0)}</label>
                                <label>&euro;{parseMoney(values.total_incl_reversed_vat)}</label>
                            </div>
                        );
                    }
                }
            );
        }
        return rows;
    }

    function renderProcessStatus() {
        if (processStatus?.status !== ProcessingStatus.WAITING) return null;
        return renderWaitingProcess();
    }

    function renderWaitingProcess() {
        const missingProducts = processStatus!.data['missing_products'];
        const dutchVat = defaultVats['NL'] ?? 21;
        const beVat = defaultVats['BE'] ?? 21;
        return (
            <div className='process-status-background'>
                <div className='process-status waiting'>
                    <div className='header-container'>
                        <label className='header-title'>
                            {t('vatDeclaration.newProductsTitle', 'Nieuwe producten gevonden!')}
                        </label>
                    </div>
                    <div className='body-container'>
                        <p>
                            {t(
                                'vatDeclaration.newProductsBody',
                                'Ons systeem heeft tijdens de berekening van de BTW aangifte {{missingProducts}} nieuwe producten gevonden die we nog niet eerder hebben gezien. Dit zijn waarschijnlijk producten van die periode destijds, die je nu niet meer actief verkoopt. Deze producten hebben we toegevoegd in Instellingen > Producten (BTW).',
                                {missingProducts}
                            )}
                            <br />
                            <br />
                            <b>
                                {t(
                                    'vatDeclaration.newProductsRateUnknown',
                                    'Van deze producten weten we het BTW tarief nog niet.'
                                )}
                                <br />
                                {t(
                                    'vatDeclaration.newProductsDefault',
                                    'Voor deze producten wordt momenteel het standaard BTW tarief per land (Nederland {{dutchVat}}%, België {{beVat}}%) gehanteerd.',
                                    {dutchVat, beVat}
                                )}
                            </b>
                        </p>
                        <br />
                        <p>
                            {t(
                                'vatDeclaration.newProductsQuestion',
                                'Wil je andere BTW tarieven instellen of wil je doorgaan met het standaard BTW tarief?'
                            )}
                        </p>
                    </div>
                    <div className='footer-container'>
                        <BluemateButton
                            onClick={onNavigateToProducts}
                            label={t('vatDeclaration.setOtherVat', 'Ander BTW tarief instellen')}
                            secondary
                        />
                        <BluemateButton
                            onClick={onRecalculate}
                            label={t('vatDeclaration.continueDefaultVat', 'Doorgaan met standaard BTW tarief')}
                        />
                    </div>
                </div>
            </div>
        );
    }

    function renderBolInvoices() {
        const invoices = getBolInvoices(declarationItems);
        return (
            <div className='bol-invoices'>
                <label className='part-label'>{t('vatDeclaration.downloads', 'Downloads')}</label>
                {renderExportProcesses()}
                <div className='invoices-container'>
                    <label className='invoices-title'>
                        {t('vatDeclaration.bolInvoicesTitle', 'Gebruikte facturen & specificaties van Bol')}
                    </label>
                    <div className='invoices-list'>
                        <div className='row head'>
                            <label>{t('vatDeclaration.invoiceHeader.period', 'PERIODE')}</label>
                            <label>{t('vatDeclaration.invoiceHeader.invoiceNumber', 'FACTUURNUMMER')}</label>
                            <div className='decl-info-container'>
                                <InfoIcon />
                                <p className='info-text'>
                                    {t(
                                        'vatDeclaration.invoiceHeader.info',
                                        'Download hier de originele Bol facturen (PDF) of factuurspecificaties (Excel).'
                                    )}
                                </p>
                            </div>
                        </div>
                        {invoices.map(renderBolInvoice)}
                        <Loader size='4rem' active={loadingExport} />
                    </div>
                </div>
            </div>
        );
    }

    function renderExportProcesses() {
        return (
            <div className='exports-container'>
                <div className='export-header'>
                    <label className='invoices-title'>
                        {t('vatDeclaration.export.title', 'BTW berekening exporteren')}
                    </label>
                    <BluemateButton
                        onClick={onExportDeclaration}
                        label={t('vatDeclaration.export.button', 'Berekening exporteren')}
                        disabled={loading || !period || !canExport}
                    />
                </div>
                <div className='export-processes'>
                    <div className='row default-table-row head'>
                        <label>{t('vatDeclaration.export.fileType', 'BESTANDSTYPE')}</label>
                        <label>{t('vatDeclaration.export.action', 'ACTIE')}</label>
                    </div>
                    {exportProcesses.map(renderExportProcess)}
                    {!exportProcesses.length && (
                        <div className='default-table-row empty'>
                            <label className='empty-label'>
                                {t('vatDeclaration.export.noneExported', '- nog geen berekeningen geëxporteerd -')}
                            </label>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    function renderExportProcess(exportProcess: ExportProcess) {
        return (
            <div key={exportProcess.id} className='row default-table-row'>
                <label>{exportProcess.translatedExportType}</label>
                {renderProcessAction(exportProcess)}
            </div>
        );
    }

    function renderProcessAction(exportProcess: ExportProcess) {
        const {status} = exportProcess;
        if (status === ExportProcessStatus.EXPORTING)
            return (
                <label className='exporting'>
                    <Loader className='status-loader' size='1rem' thickness='.2rem' active disableBackground />{' '}
                    {t('vatDeclaration.export.exporting', 'Bezig met exporteren...')}
                </label>
            );
        else if (status === ExportProcessStatus.ERROR)
            return <label className='error'>{t('vatDeclaration.export.error', 'Fout')}</label>;
        else
            return (
                <label
                    className={classNames('download', !canExport && 'cursor-default')}
                    onClick={canExport ? onDownloadExport(exportProcess) : undefined}
                >
                    {t('vatDeclaration.export.download', 'Downloaden')}
                </label>
            );
    }

    function renderBolInvoice(bolInvoice: BolInvoice, index: number) {
        const isDemo = context.userContext.user?.isDemoAccount === true;
        const [periodStart, periodEnd] = determineInvoicePeriod(bolInvoice);
        const period =
            periodStart.format(DUTCH_DATE_FORMAT) +
            ' ' +
            t('vatDeclaration.until', 't/m') +
            ' ' +
            periodEnd.format(DUTCH_DATE_FORMAT);
        return (
            <div className='row' key={index}>
                <label>{period}</label>
                <label>{bolInvoice.invoice_id}</label>
                <div className='download-actions'>
                    {!isDemo && (
                        <AwesomeIcon
                            name='file-pdf'
                            className={classNames('download-icon pdf', !canDownload && 'disabled cursor-default')}
                            onClick={canDownload ? onDownloadInvoice(bolInvoice.invoice_id, 'pdf') : undefined}
                            title={t('vatDeclaration.downloadPdfTitle', 'Download PDF factuur')}
                        />
                    )}
                    <AwesomeIcon
                        name='file-excel'
                        className={classNames('download-icon excel', !canDownload && 'disabled cursor-default')}
                        onClick={canDownload ? onDownloadInvoice(bolInvoice.invoice_id, 'excel') : undefined}
                        title={t('vatDeclaration.downloadExcelTitle', 'Download Excel specificatie')}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <InfoBanner
                featureKey='vat_declaration'
                variant='info'
                title={t('vatDeclaration.banner.title', 'BTW-aangifte')}
                showForNewUsersOnly={true}
            >
                {t(
                    'vatDeclaration.banner.body',
                    'Bereken in één klik je btw bedragen over je bol verkopen. Let op: laat controleren door jouw boekhouder.'
                )}
            </InfoBanner>
            <div className='vat-declaration'>
                {showInfo && renderInfo()}
                <VatPeriodSelectController onChange={onChangePeriod} loading={loading} />
                {renderPeriod()}
                {showReverseDisclaimer && renderReverseDisclaimer()}
                {renderLastOrderDate()}
                {renderProcessStatus()}
                {renderGeneralWarnings()}
                <div className='result-container'>
                    {renderTotals()}
                    {renderLists()}
                    {renderBolInvoices()}
                </div>
            </div>
        </>
    );
}
